/**
 * Role names as seeded in uc-core (snake_case, like the existing `admin`, `field_supervisor`).
 * The F4/F5 roles come from task F4.1-p (role × action × scope matrix); align the strings here
 * when the seed lands — nothing else in the app hard-codes a role name.
 */
export const ROLES = {
  ADMIN: 'admin',
  CENTRAL_ADMIN: 'central_admin',
  SUPERVISOR: 'supervisor',
  VILLAGE_OPERATOR: 'village_operator',
  STORE_KEEPER: 'store_keeper',
  MACHINE_OPERATOR: 'machine_operator',
  PAYMENTS_APPROVER: 'payments_approver',
  QUALITY_RELEASE: 'quality_release',
} as const
export type RoleName = (typeof ROLES)[keyof typeof ROLES] | (string & {})

/** Roles that work on the phone surface (/m). */
export const PHONE_ROLES: readonly RoleName[] = [
  ROLES.VILLAGE_OPERATOR,
  ROLES.STORE_KEEPER,
  ROLES.SUPERVISOR,
  ROLES.MACHINE_OPERATOR,
  ROLES.ADMIN,
]

/** Roles that work in the office console (/c). */
export const CONSOLE_ROLES: readonly RoleName[] = [
  ROLES.SUPERVISOR,
  ROLES.PAYMENTS_APPROVER,
  ROLES.QUALITY_RELEASE,
  ROLES.CENTRAL_ADMIN,
  ROLES.ADMIN,
]

/** Superusers pass every role check. */
const SUPER_ROLES: readonly RoleName[] = [ROLES.ADMIN, ROLES.CENTRAL_ADMIN]

export function hasAnyRole(
  userRoles: readonly string[],
  required: readonly RoleName[] | undefined,
): boolean {
  if (!required || required.length === 0) return true
  if (userRoles.some((r) => SUPER_ROLES.includes(r))) return true
  return userRoles.some((r) => required.includes(r))
}

export type Surface = 'phone' | 'console'

/**
 * Where a signed-in person lands. Console roles win (a supervisor at a desk); a supervisor can
 * still open /m on a phone. Someone with neither gets the no-access page.
 */
export function landingPath(userRoles: readonly string[], prefer?: Surface): string {
  const phone = userRoles.some((r) => PHONE_ROLES.includes(r))
  const console_ = userRoles.some((r) => CONSOLE_ROLES.includes(r))
  if (prefer === 'phone' && phone) return '/m'
  if (prefer === 'console' && console_) return '/c'
  if (console_) return '/c'
  if (phone) return '/m'
  return '/no-access'
}

/**
 * The admin role may act on its own work (two-person rule override, R11); the server lets it
 * through and records the override. Money (payouts, bank accounts, advances) keeps the rule.
 */
export function canOverrideTwoPerson(
  userRoles: ReadonlyArray<string | { name: string }> | undefined,
): boolean {
  return Boolean(userRoles?.some((r) => (typeof r === 'string' ? r : r.name) === ROLES.ADMIN))
}
